/**
 * "图层/通道/路径"tab 容器（DESIGN.md §7.1 补充，参照 Photoshop）。
 *
 * `AppShell.tsx` 的面板是纵向堆叠的独立 `<section>`，不是 tab 容器——这三个
 * 面板要在同一块地方切换，靠这个组件在 `panelSlots.layers` 那一个槽位里
 * 自己管一套 tab 状态，`AppShell.tsx` 本身不用改。纯展示组件：不认识
 * store，三个子面板整个作为 JSX 传进来，跟 `LayersPanel` 同一个"文档树进、
 * 回调出"的分层规则。
 */
import type { JSX } from 'solid-js';
import { For, Show, createSignal } from 'solid-js';
import styles from './LayerChannelPathTabs.module.css';

export type PanelTab = 'layers' | 'channels' | 'paths';

const TABS: ReadonlyArray<{ readonly id: PanelTab; readonly label: string }> = [
  { id: 'layers', label: '图层' },
  { id: 'channels', label: '通道' },
  { id: 'paths', label: '路径' },
];

export interface LayerChannelPathTabsProps {
  /** 受控当前 tab；缺省时组件自己管一份内部状态。 */
  readonly activeTab?: PanelTab | undefined;
  readonly onTabChange?: ((tab: PanelTab) => void) | undefined;
  readonly layers: JSX.Element;
  readonly channels: JSX.Element;
  readonly paths: JSX.Element;
}

export function LayerChannelPathTabs(props: LayerChannelPathTabsProps): JSX.Element {
  const [internalTab, setInternalTab] = createSignal<PanelTab>('layers');
  const active = (): PanelTab => props.activeTab ?? internalTab();

  return (
    <div class={styles.container} data-testid="layer-channel-path-tabs">
      <div class={styles.tabBar} role="tablist" aria-label="图层、通道、路径">
        <For each={TABS}>
          {(tab) => (
            <button
              type="button"
              class={styles.tabButton}
              classList={{ [styles.tabActive!]: active() === tab.id }}
              role="tab"
              aria-selected={active() === tab.id}
              data-testid={`panel-tab-${tab.id}`}
              onClick={() => {
                setInternalTab(tab.id);
                props.onTabChange?.(tab.id);
              }}
            >
              {tab.label}
            </button>
          )}
        </For>
      </div>
      <div class={styles.body}>
        <Show when={active() === 'layers'}>{props.layers}</Show>
        <Show when={active() === 'channels'}>{props.channels}</Show>
        <Show when={active() === 'paths'}>{props.paths}</Show>
      </div>
    </div>
  );
}
